import AiStreamResponse from "@/utils/AiStreamResponse";
import { generateChatBotReferalLetterUrl } from "@/apiUrls/apiUrls";
import { useTitanApp } from "@/hooks/useTitanApp";
import { getPharmacyContactDetails } from "@/dispatchers/pharmacyServiceDispatchers";

export const useReferralLetterData = () => {
  const { appState, patientState, pharmacyServiceState } = useTitanApp();
  const { companyId, currentUser, currentPharmacy, company } = appState || {};
  const {
    patientConsultations,
    showPatientConsultationsLoader,
    patientPrescriptions,
    patientPrescriptionsLoader,
    selectedPatientDetails,
    selectedPatientId,
    patientVaccinations,
    selectedPharmacyId,
    consultationId,
    referralCompleted,
    showReferralLetterDialog,
    selectedPatientConsultation,
  } = patientState || {};
  const { pharmacyContactDetails } = pharmacyServiceState || {};
  const applyTemplatePlaceholders = (
    content: string,
    dateStr: string,
  ): string => {
    const dob = selectedPatientDetails?.dob
      ? new Intl.DateTimeFormat("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        }).format(new Date(selectedPatientDetails?.dob))
      : "";
    const replacements: Record<string, string> = {
      "{{DATE}}": dateStr,
      "{{PATIENT_NAME}}":
        selectedPatientDetails?.patientName ||
        [
          selectedPatientDetails?.title,
          selectedPatientDetails?.foreName,
          selectedPatientDetails?.surName,
        ]
          .filter(Boolean)
          .join(" ") ||
        "",
      "{{patient.title}}": selectedPatientDetails?.title || "",
      "{{patient.firstName}}":
        selectedPatientDetails?.foreName ||
        ReferralLetterData.placeholderDefaults.patientFirstName,
      "{{patient.dob}}":
        dob || ReferralLetterData.placeholderDefaults.patientDob,
      "{{patient.address}}":
        selectedPatientDetails?.address ||
        ReferralLetterData.placeholderDefaults.PatientAddress,
      "{{patient.lastName}}":
        selectedPatientDetails?.surName ||
        ReferralLetterData.placeholderDefaults.patientLastName,
      "{{patient.email}}":
        selectedPatientDetails?.email ||
        ReferralLetterData.placeholderDefaults.patientEmail,
      "{{patient.phone}}":
        selectedPatientDetails?.contacts === null
          ? "-"
          : selectedPatientDetails?.contacts[0]?.value ||
            "-" ||
            ReferralLetterData.placeholderDefaults.patientPhone,
      "{{appointment.date}}":
        ReferralLetterData.placeholderDefaults.appointmentDate,
      "{{appointment.time}}":
        ReferralLetterData.placeholderDefaults.appointmentTime,
      "{{appointment.duration}}":
        ReferralLetterData.placeholderDefaults.appointmentDuration,
      "{{appointment.service}}":
        selectedPatientConsultation?.serviceName ||
        ReferralLetterData.placeholderDefaults.appointmentService,
      "{{practice.name}}":
        currentPharmacy?.teamPharmacyName ||
        ReferralLetterData.placeholderDefaults.PracticeName,
      "{{practice.email}}":
        pharmacyContactDetails?.emailAddress ||
        ReferralLetterData.placeholderDefaults.PracticeEmail,
      "{{practice.phone}}":
        pharmacyContactDetails?.phone ||
        ReferralLetterData.placeholderDefaults.PracticePhone,
      "{{practice.address}}":
        currentPharmacy?.teamPharmacyAddress ||
        ReferralLetterData.placeholderDefaults.PracticeAddress,
      "{{pharmacy.name}}":
        currentPharmacy?.name ||
        ReferralLetterData.placeholderDefaults.PracticeName,
      "{{pharmacy.phone}}":
        pharmacyContactDetails?.phone ||
        ReferralLetterData.placeholderDefaults.PracticePhone,
      "{{pharmacy.address}}":
        currentPharmacy?.address ||
        ReferralLetterData.placeholderDefaults.PracticeAddress,
      "{{location.name}}":
        currentPharmacy?.teamPharmacyName ||
        ReferralLetterData.placeholderDefaults.PracticeName,
      "{{location.address}}":
        currentPharmacy?.teamPharmacyAddress ||
        ReferralLetterData.placeholderDefaults.PracticeAddress,
      "{{prescriber.name}}":
        currentUser?.fullName ||
        ReferralLetterData.placeholderDefaults.PrescriberName,
      "{{prescriber.title}}":
        currentUser?.title ||
        ReferralLetterData.placeholderDefaults.PrescriberTitle,
      "{{bookingLink}}": "",
      "{{link.reschedule}}":
        ReferralLetterData.placeholderDefaults.rescheduleLink,
      "{{link.cancel}}": "",
    };

    const escapeRegExp = (value: string) =>
      value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    let updated = content;
    Object.entries(replacements).forEach(([token, value]) => {
      updated = updated.replace(new RegExp(escapeRegExp(token), "g"), value);
    });
    return updated;
  };

  return {
    companyId,
    currentUser,
    currentPharmacy,
    company,
    patientConsultations,
    showPatientConsultationsLoader,
    patientPrescriptions,
    patientPrescriptionsLoader,
    selectedPatientDetails,
    selectedPatientId,
    patientVaccinations,
    selectedPharmacyId,
    consultationId,
    referralCompleted,
    showReferralLetterDialog,
    applyTemplatePlaceholders,
  };
};

type ReferralTemplateType = "email" | "sms" | "letter" | "both";

export interface ReferralTemplate {
  id: string;
  name: string;
  type: ReferralTemplateType;
  subject?: string;
  content: string;
  actionType?: number;
}

interface ClinicalContext {
  chiefComplaint: string;
  findings: string;
  medications: string;
  history: string;
  assessment: string;
  plan: string;
}

interface ReferralLetterApiResult {
  title: string;
  content: string;
}

export class ReferralLetterData {
  static readonly placeholderDefaults = {
    patientName: "[Patient Name]",
    patientTitle: "[Title]",
    patientFirstName: "[First Name]",
    patientLastName: "[Last Name]",
    patientEmail: "[patient@email.com]",
    patientPhone: "[Phone]",
    appointmentTime: "[Time]",
    appointmentDuration: "[Duration]",
    appointmentService: "[Service]",
    bookingLink: "[Booking Link]",
    rescheduleLink: "[Reschedule Link]",
    cancelLink: "[Cancel Link]",
    appointmentDate: "[Date]",
    patientDob: "[Dob]",
    PatientAddress: "[Address]",
    PracticeName: "[Practice Name]",
    PracticeAddress: "[Practice address]",
    PracticeEmail: "[Practice Email]",
    PracticePhone: "[Practice Phone]",
    PrescriberName: "[Prescriber Name]",
    PrescriberTitle: "[Prescriber title]",
  };

  static formatDate(date: Date = new Date()): string {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }

  static normalizeTemplateType(value: any): ReferralTemplateType {
    if (value === 0 || value === "0") return "email";
    if (value === 1 || value === "1") return "sms";
    if (value === 2 || value === "2") return "letter";
    if (typeof value === "string") {
      const normalized = value.toLowerCase();
      if (
        normalized === "email" ||
        normalized === "sms" ||
        normalized === "letter" ||
        normalized === "both"
      ) {
        return normalized as ReferralTemplateType;
      }
    }
    return "email";
  }

  static toDocumentType(
    type: ReferralTemplateType,
  ): "email" | "sms" | "letter" {
    if (type === "both") return "email";
    return type;
  }

  static mapMessageTemplates(messageTemplates: any[]): ReferralTemplate[] {
    if (!Array.isArray(messageTemplates)) return [];
    return messageTemplates.map((s: any) => ({
      id: s._id || "",
      name: s?.templateName || "",
      type: ReferralLetterData.normalizeTemplateType(s?.messageType),
      subject: s?.subject || "",
      content: s?.text || "",
      actionType: s?.actionType || "",
    }));
  }

  static extractClinicalContext(transcription: string): ClinicalContext {
    const lower = transcription.toLowerCase();

    const extractSection = (headings: string[]): string => {
      for (const heading of headings) {
        const idx = lower.indexOf(heading.toLowerCase());
        if (idx !== -1) {
          const start = idx + heading.length;
          const rest = transcription.slice(start);
          const match = rest.match(/\n[A-Z][A-Z\s]+:/);
          const end = match?.index ?? rest.length;
          return rest
            .slice(0, end)
            .replace(/^[:\s]+/, "")
            .trim();
        }
      }
      return "";
    };

    return {
      chiefComplaint: extractSection([
        "CHIEF COMPLAINT:",
        "PRESENTING COMPLAINT:",
        "Chief Complaint:",
      ]),
      findings: extractSection([
        "PHYSICAL EXAMINATION:",
        "EXAMINATION FINDINGS:",
        "OBJECTIVE:",
        "CLINICAL FINDINGS:",
      ]),
      medications: extractSection([
        "CURRENT MEDICATIONS:",
        "MEDICATIONS:",
        "PLAN:",
      ]),
      history: extractSection([
        "HISTORY OF PRESENT ILLNESS:",
        "PAST MEDICAL HISTORY:",
        "SUBJECTIVE:",
      ]),
      assessment: extractSection([
        "ASSESSMENT:",
        "DIAGNOSIS:",
        "CLINICAL IMPRESSION:",
      ]),
      plan: extractSection(["TREATMENT PLAN:", "PLAN:"]),
    };
  }

  static getTranscriptionPreview(transcription: string): string {
    if (!transcription?.trim()) return "";
    const ctx = ReferralLetterData.extractClinicalContext(transcription);
    if (ctx.chiefComplaint) {
      return ctx.chiefComplaint.length > 80
        ? ctx.chiefComplaint.slice(0, 80) + "..."
        : ctx.chiefComplaint;
    }

    const firstLine = transcription
      .trim()
      .split("\n")
      .find((line) => line.trim().length > 10);

    if (firstLine) {
      return firstLine.trim().length > 80
        ? firstLine.trim().slice(0, 80) + "..."
        : firstLine.trim();
    }

    return "Consultation transcription available";
  }

  static async generateLetterFromApi(params: {
    prompt: string;
    patientName?: string;
    dob?: string;
    clinicianName?: string;
    apiUrl?: string;
  }): Promise<ReferralLetterApiResult> {
    let accumulatedText = "";
    let finalTitle = "";
    let finalContent = "";

    const apiUrl = params.apiUrl || generateChatBotReferalLetterUrl();

    await AiStreamResponse(
      { prompt: params.prompt },
      (chunk: string) => {
        accumulatedText += chunk;
        const cleaned = accumulatedText.replace(/[{}]/g, "").trim();

        const titleMatch = cleaned.match(/"Title"\s*:\s*"([^"]*)"/);
        const contentMatch = cleaned.match(/"Content"\s*:\s*"([\s\S]*)$/);

        if (titleMatch && !finalTitle) {
          finalTitle = titleMatch[1];
        }

        if (contentMatch) {
          finalContent = contentMatch[1]
            .replace(/\\"/g, '"')
            .replace(/\\n/g, "\n")
            .replace(/\[Patient Name\]/gi, params.patientName || "")
            .replace(/\[DOB\]/gi, params.dob || "[DOB]")
            .replace(
              /\[YOUR_NAME_PLACEHOLDER\]/gi,
              params.clinicianName || "[YOUR_NAME_PLACEHOLDER]",
            )
            .replace(/```/g, "")
            .replace(/"\s*$/, "");
        }
      },
      () => {},
      apiUrl,
    );

    return {
      title: finalTitle || "Referral Letter",
      content: finalContent || "",
    };
  }
}
